import { z } from 'zod';
import {
  ChannelSchema,
  ProductGroupSchema,
  type HistoricalOrder,
} from '@/domain';
import { mockStore } from '@/lib/mock-store';
import { getSupabase, isMockBackend } from '@/lib/supabase';
import { warehouseService } from './warehouseService';
import { customerService } from './customerService';
import { auditService } from './auditService';
import { parseSpreadsheet, type ParseSummary } from './xlsxParser';

/**
 * Public schema for an import row. Caller spreadsheet must use either
 * the friendly VN headers below (Bx-flavored) or the raw IDs.
 *
 * Required: warehouse_code, customer_code, date (YYYY-MM-DD or Excel date), qty
 * Optional: channel, product_group
 */
const ImportRowSchema = z.object({
  warehouse_code: z.string().min(1),
  customer_code: z.string().min(1),
  date: z
    .union([z.string(), z.number()])
    .transform((v) => coerceDate(v))
    .pipe(z.string()),
  qty: z
    .union([z.number(), z.string()])
    .transform((v) => Number(v))
    .pipe(z.number().int().nonnegative()),
  channel: ChannelSchema.nullable().optional().default(null),
  product_group: ProductGroupSchema.nullable().optional().default(null),
});
type ImportRow = z.output<typeof ImportRowSchema>;

const HEADER_MAP: Record<string, string> = {
  // VN headers from the public template
  'Mã kho': 'warehouse_code',
  'Mã khách hàng': 'customer_code',
  'Ngày': 'date',
  'Số lượng': 'qty',
  'Kênh': 'channel',
  'Nhóm sản phẩm': 'product_group',
  // Also accept English
  warehouse: 'warehouse_code',
  customer: 'customer_code',
  Date: 'date',
  Qty: 'qty',
  Channel: 'channel',
  ProductGroup: 'product_group',
};

function coerceDate(v: string | number): string {
  if (typeof v === 'string') {
    // Excel might give e.g. "2026-05-01" already
    if (/^\d{4}-\d{2}-\d{2}/.test(v)) return v.slice(0, 10);
    // Some sheets use DD/MM/YYYY
    const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(v);
    if (m) {
      const dd = m[1]!.padStart(2, '0');
      const mm = m[2]!.padStart(2, '0');
      return `${m[3]}-${mm}-${dd}`;
    }
    return v;
  }
  // Excel serial date number → JS Date (xlsx default 1900 system)
  const d = XLSX_DATE_EPOCH(v);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function XLSX_DATE_EPOCH(serial: number): Date {
  // Account for Excel's leap year bug — serial 60 maps to 1900-02-29 (fictitious)
  const utcDays = Math.floor(serial - 25569);
  const utcValue = utcDays * 86400;
  const date = new Date(utcValue * 1000);
  return date;
}

export interface ImportPreviewRow {
  ok: boolean;
  row: number;
  warehouse_code: string;
  customer_code: string;
  date: string;
  qty: number;
  status: 'new' | 'update' | 'unchanged' | 'error';
  prior_qty?: number;
  warehouse_id?: string;
  customer_id?: string;
  errors?: string[];
}

export interface ImportPreview {
  summary: ParseSummary<ImportRow>;
  rows: ImportPreviewRow[];
  counts: { new: number; update: number; unchanged: number; error: number };
}

export const historicalImportService = {
  /**
   * Parse a file + resolve warehouse/customer codes + diff against existing rows.
   * Pure preview — does NOT mutate the store.
   */
  async preview(file: ArrayBuffer): Promise<ImportPreview> {
    const summary = parseSpreadsheet(file, ImportRowSchema, { headerMap: HEADER_MAP });

    const warehouses = await warehouseService.list();
    const customers = await customerService.list();
    const whByCode = new Map(warehouses.map((w) => [w.code, w]));
    const custByCode = new Map(customers.map((c) => [c.code, c]));

    const existing = isMockBackend
      ? mockStore.getAll('historical_orders')
      : await (async () => {
          const sb = getSupabase()!;
          const { data, error } = await sb.from('historical_orders').select('*');
          if (error) throw error;
          return (data ?? []) as HistoricalOrder[];
        })();

    const existingMap = new Map<string, HistoricalOrder>();
    for (const h of existing) {
      existingMap.set(`${h.warehouse_id}|${h.customer_id}|${h.date}`, h);
    }

    const rows: ImportPreviewRow[] = summary.rows.map((r) => {
      if (!r.ok || !r.data) {
        return {
          ok: false,
          row: r.row,
          warehouse_code: String(r.raw.warehouse_code ?? ''),
          customer_code: String(r.raw.customer_code ?? ''),
          date: String(r.raw.date ?? ''),
          qty: Number(r.raw.qty ?? 0),
          status: 'error',
          errors: r.errors,
        };
      }
      const wh = whByCode.get(r.data.warehouse_code);
      const cust = custByCode.get(r.data.customer_code);
      const errs: string[] = [];
      if (!wh) errs.push(`Mã kho không tồn tại: ${r.data.warehouse_code}`);
      if (!cust) errs.push(`Mã khách hàng không tồn tại: ${r.data.customer_code}`);
      if (errs.length) {
        return {
          ok: false,
          row: r.row,
          warehouse_code: r.data.warehouse_code,
          customer_code: r.data.customer_code,
          date: r.data.date,
          qty: r.data.qty,
          status: 'error',
          errors: errs,
        };
      }
      const prior = existingMap.get(`${wh!.id}|${cust!.id}|${r.data.date}`);
      let status: ImportPreviewRow['status'];
      if (!prior) status = 'new';
      else if (prior.qty === r.data.qty) status = 'unchanged';
      else status = 'update';
      return {
        ok: true,
        row: r.row,
        warehouse_code: r.data.warehouse_code,
        customer_code: r.data.customer_code,
        date: r.data.date,
        qty: r.data.qty,
        warehouse_id: wh!.id,
        customer_id: cust!.id,
        prior_qty: prior?.qty,
        status,
      };
    });

    const counts = {
      new: rows.filter((r) => r.status === 'new').length,
      update: rows.filter((r) => r.status === 'update').length,
      unchanged: rows.filter((r) => r.status === 'unchanged').length,
      error: rows.filter((r) => r.status === 'error').length,
    };

    return { summary, rows, counts };
  },

  /**
   * Apply a previously previewed import. Only rows with status 'new' or 'update'
   * (and ok=true) are written. Audit log entry is emitted for the batch.
   */
  async commit(
    preview: ImportPreview,
    actor: string,
    opts?: { dryRun?: boolean },
  ): Promise<{ inserted: number; updated: number; skipped: number }> {
    const applicable = preview.rows.filter(
      (r) => r.ok && (r.status === 'new' || r.status === 'update'),
    );
    if (opts?.dryRun) {
      return {
        inserted: preview.counts.new,
        updated: preview.counts.update,
        skipped: preview.counts.unchanged + preview.counts.error,
      };
    }

    let inserted = 0;
    let updated = 0;

    if (isMockBackend) {
      const current = mockStore.getAll('historical_orders');
      const byKey = new Map<string, HistoricalOrder>();
      for (const h of current) {
        byKey.set(`${h.warehouse_id}|${h.customer_id}|${h.date}`, h);
      }
      for (const r of applicable) {
        const k = `${r.warehouse_id}|${r.customer_id}|${r.date}`;
        if (r.status === 'new') {
          inserted++;
          const row: HistoricalOrder = {
            id: `h_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
            warehouse_id: r.warehouse_id!,
            customer_id: r.customer_id!,
            date: r.date,
            qty: r.qty,
            channel: null,
            product_group: null,
            source: 'IMPORT_XLSX',
            imported_at: new Date().toISOString(),
            imported_by: actor,
          };
          byKey.set(k, row);
        } else {
          updated++;
          const prior = byKey.get(k);
          if (prior) {
            byKey.set(k, { ...prior, qty: r.qty, source: 'IMPORT_XLSX', imported_by: actor });
          }
        }
      }
      mockStore.replace('historical_orders', Array.from(byKey.values()));
    } else {
      const sb = getSupabase()!;
      for (const r of applicable) {
        if (r.status === 'new') inserted++;
        else updated++;
        const { error } = await sb.from('historical_orders').upsert(
          {
            warehouse_id: r.warehouse_id,
            customer_id: r.customer_id,
            date: r.date,
            qty: r.qty,
            channel: null,
            product_group: null,
            source: 'IMPORT_XLSX',
            imported_by: actor,
          },
          { onConflict: 'warehouse_id,customer_id,date,channel,product_group' },
        );
        if (error) throw error;
      }
    }

    await auditService.log({
      actor,
      action: 'IMPORT',
      entity_type: 'historical_orders',
      summary: `Import ${inserted} dòng mới + cập nhật ${updated} dòng (bỏ qua ${preview.counts.unchanged + preview.counts.error}).`,
      payload: { ...preview.counts, total: preview.summary.totalCount },
    });

    return { inserted, updated, skipped: preview.counts.unchanged + preview.counts.error };
  },
};
